import React, { useEffect, useMemo, useState } from "react";
import {
  useOutletContext,
  useRouteLoaderData,
  useSearchParams,
} from "react-router-dom";
import { SnackbarProvider } from "notistack";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
} from "@mui/material";
import { BACKEND_URL, CONSUMER_URL } from "../../helpers/variables";

import Normal from "./Table/Normal";
import Custom from "./Table/Custom";
import { useBill } from "../../Components/Contexts/Bill";

import { endOfDay, parse, parseISO, startOfDay } from "date-fns";

import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite-no-reset.css";

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
})();

export const statusMap = {
  0: { name: "Partial Read", key: "POSTING_FAILED", color: "secondary" },
  1: { name: "Posted", key: "APPROVAL", color: "success", hide: true },
  3: { name: "Deleted", key: "DELETE", color: "warning", hide: true },
  "-1": {
    name: "Extraction failed",
    key: "OCR_FAILED",
    color: "error",
    hide: true,
  },
  "-2": {
    name: "Extraction failed",
    key: "EXTRACTION_FAILED",
    color: "warning",
  },
  "-3": { name: "Invalid file", key: "INVALID_FILE", color: "error" },
  "-4": {
    name: "Extraction failed",
    key: "GROUPING_FAILED",
    color: "warning",
    hide: true,
  },
};

export const lobTabs = [
  {
    value: "pending",
    label: "Pending Log",
  },
  {
    value: "email",
    label: "Email log",
  },
];

const InboxPage = () => {
  const { bill, loading, setLoading } = useBill();

  const { restrictedVendorIds } = useOutletContext();

  const [searchParams] = useSearchParams();

  const clientURL = searchParams.get("client_url");
  const corporationID = searchParams.get("corporation_id");
  const fromDate = searchParams.get("from_date");
  const toDate = searchParams.get("to_date");

  const defaultFilters = {
    dateRange:
      fromDate && toDate
        ? [new Date(fromDate), endOfDay(new Date(toDate))]
        : [startOfDay(new Date()), endOfDay(new Date())],
    status: "all",
    tab: "pending",
    stream_count: 1,
  };

  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState(
    searchParams.get("vendor_name") || "All",
  );
  const [filters, setFilters] = useState(defaultFilters);

  const { dateRange, status, tab } = filters;
  const [startDate, endDate] =
    dateRange?.map((date) => date.toISOString()) || [];

  const basePayload = {
    corporation_id: corporationID,
    client_url: clientURL,
    tab,
    restricted_vendor_ids: restrictedVendorIds.join(","),
    ...(status !== "queue" && { status }),
    ...(startDate && endDate && { start_date: startDate, end_date: endDate }),
  };

  useEffect(() => {
    if (!bill) return;

    console.log(bill);

    let { _id, bill_status, vendor, error_description, vendor_status } = bill;

    if (!isInIframe) {
      window.open(`/grouping?bill_id=${_id}`, "_blank", "noopener,noreferrer");
      return;
    }

    let screen;

    let errors = error_description?.split(/\r?\n/) || [];

    console.log(errors);

    if (bill_status === 0) {
      if (!vendor_status && errors.length > 1) {
        screen = "both";
      } else if (!vendor_status) {
        screen = "vendor";
      } else {
        screen = "form";
      }
    } else if (bill_status === -1) {
      screen = "form";
    } else if ([-2, -4].includes(bill_status)) {
      screen = "io";
    }

    if (!screen) return;

    console.log(screen);

    window.parent.postMessage(
      JSON.stringify({
        source: "nimbleio",
        screen,
        bill_id: _id,
      }),
      "*",
    );
    console.log("Message posted");
  }, [bill]);

  useEffect(() => {
    getVendors();
  }, [filters]);

  let eventSource;
  useEffect(() => {
    getBills();
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [filters, vendor]);

  async function getVendors() {
    setVendor("All");
    setLoading(true);
    let res = await fetch(`${BACKEND_URL}/vendor_list`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(basePayload),
    });
    setLoading(false);
    setVendors(await res.json());
  }

  async function getBills() {
    let params = new URLSearchParams({
      ...basePayload,
      vendor_name: vendor,
    }).toString();

    setLoading(true);
    eventSource = new EventSource(`${BACKEND_URL}/stream_bills?${params}`);

    eventSource.onmessage = (event) => {
      let bills = JSON.parse(event.data);
      setBills(bills);
      setLoading(false);
    };

    eventSource.onerror = () => {
      eventSource.close();
      setLoading(false);
    };
  }

  const updateFilter = (key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  function refresh() {
    setFilters((prev) => ({ ...prev, stream_count: prev.stream_count + 1 }));
  }

  return (
    <SnackbarProvider
      anchorOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Stack height={"100%"} gap={1}>
        {/* Head (Filters) */}
        <Toolbar disableGutters variant="dense" sx={{ gap: 2 }}>
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <MenuItem value={"all"}>All</MenuItem>
            <MenuItem value={"queue"}>In process</MenuItem>
            {Object.entries(statusMap).map(
              ([status, stats], index) =>
                !stats["hide"] && (
                  <MenuItem key={index} value={status}>
                    {stats.name}
                  </MenuItem>
                ),
            )}
          </TextField>

          <Autocomplete
            options={["All", ...vendors]}
            value={vendor}
            onChange={(e, v) => setVendor(v)}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} label="Search vendor" />
            )}
          />

          <Box flexGrow={1}>
            <DateRangePicker
              size="lg"
              value={filters.dateRange}
              placeholder="From & To"
              onChange={(v) =>
                updateFilter("dateRange", v ? [v[0], endOfDay(v[1])] : v)
              }
              showHeader={false}
              format={"MM/dd/yyyy"}
            />
          </Box>

          <ButtonGroup>
            {lobTabs.map((option) => (
              <Button
                variant={
                  option.value === filters.tab ? "contained" : "outlined"
                }
                color="success"
                sx={{
                  px: 5,
                  py: 0.3,
                  color: "#118785",
                  border: "1px solid #118785",
                }}
                onClick={() => {
                  if (option.value === filters.tab) {
                    refresh();
                  } else {
                    setFilters({ ...defaultFilters, tab: option.value });
                  }
                }}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Toolbar>

        {/* Body */}
        {filters.tab === "email" ? (
          <Normal data={bills || []} refresh={refresh} />
        ) : (
          <Custom data={bills || []} refresh={refresh} />
        )}
      </Stack>

      <Backdrop open={loading} sx={{ zIndex: 3 }}>
        <CircularProgress />
      </Backdrop>
    </SnackbarProvider>
  );
};

export default InboxPage;
